import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import Goal from "../models/Goal";
import GoalAttempt from "../models/GoalAttempt";

const GoalsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [goalStats, setGoalStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // "active" | "completed" | "all"

  useEffect(() => {
    if (user) {
      loadGoals();
    }
  }, [user]);

  const loadGoals = async () => {
    try {
      const goalList = await Goal.getAll(user.uid);
      setGoals(goalList);

      // Load stats for each goal
      const stats = {};
      await Promise.all(
        goalList.map(async (goal) => {
          stats[goal.id] = await GoalAttempt.getStats(user.uid, goal.id, goal);
        })
      );
      setGoalStats(stats);
    } catch (err) {
      console.error("Error loading goals:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredGoals = goals.filter((goal) => {
    if (filter === "all") return true;
    return goal.status === filter;
  });

  // Sort by deadline (soonest first for active, most recent for completed)
  const sortedGoals = [...filteredGoals].sort((a, b) => {
    const dateA = a.deadline?.toDate ? a.deadline.toDate() : new Date(a.deadline);
    const dateB = b.deadline?.toDate ? b.deadline.toDate() : new Date(b.deadline);
    if (filter === "completed") {
      return dateB - dateA;
    }
    return dateA - dateB;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          Loading goals...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-2"
          >
            <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            My Goals
          </h1>
        </div>
        <button
          onClick={() => navigate("/add-goal")}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add New
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {["active", "completed", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === f
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {sortedGoals.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-md">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {filter === "active" ? "No active goals" : filter === "completed" ? "No completed goals" : "No goals yet"}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Set a goal with a deadline and track your progress
          </p>
          <button
            onClick={() => navigate("/add-goal")}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
          >
            Create Goal
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedGoals.map((goal) => {
            const stats = goalStats[goal.id] || {};
            const currentValue = stats.currentValue ?? goal.startingValue ?? 0;
            const progress = Goal.calculateProgress(goal, currentValue);
            const daysRemaining = Goal.getDaysRemaining(goal);
            const paceStatus = Goal.getPaceStatus(goal, currentValue);
            const isCompleted = goal.status === "completed";

            return (
              <div
                key={goal.id}
                onClick={() => navigate(`/goal/${goal.id}`)}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex gap-4">
                  {goal.imgThumb && (
                    <img
                      src={goal.imgThumb}
                      alt={goal.title}
                      className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                        {goal.title}
                      </h3>
                      {isCompleted ? (
                        <span className="flex-shrink-0 px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full">
                          Complete
                        </span>
                      ) : (
                        <span className={`flex-shrink-0 px-2 py-1 text-xs font-medium ${paceStatus.bgColor} ${paceStatus.color} rounded-full`}>
                          {paceStatus.label}
                        </span>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600 dark:text-gray-400">
                          {currentValue} / {goal.targetValue} {goal.targetUnit}
                          {goal.targetReps && ` x${goal.targetReps}`}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {Math.round(progress)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full ${isCompleted ? "bg-green-500" : "bg-indigo-600"}`}
                          style={{ width: `${Math.min(100, progress)}%` }}
                        />
                      </div>
                    </div>

                    {/* Meta Info */}
                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500 dark:text-gray-400">
                      {isCompleted ? (
                        <span>Completed {formatDate(goal.completedAt)}</span>
                      ) : (
                        <>
                          <span>{daysRemaining} days left</span>
                          <span>Due {formatDate(goal.deadline)}</span>
                        </>
                      )}
                      {stats.latestAttempt && (
                        <span>Latest: {stats.latestAttempt.value}{goal.targetUnit}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GoalsPage;
